/**
 * Zustand Auth Store — PIN-based authentication
 *
 * De sessie staat in `localStorage` met een inactiviteitsklok, niet in
 * `sessionStorage`.
 *
 * Waarom (11-08): `sessionStorage` hangt aan het tabblad, en Chrome op Android
 * gooit een tabblad op de achtergrond weg zodra het geheugen nodig heeft. De
 * chauffeur legt de telefoon in de houder, gebruikt Maps of WhatsApp, komt
 * terug, en dan is het tabblad ververst, de opslag leeg en staat hij op het
 * loginscherm. Dat gebeurde bij meerdere
 * chauffeurs "om de haverklap". Serverzijdig was er niets aan de hand: sinds de
 * fix van 28-07 staat er geen enkele `pin_ongeldig` of `backend` in
 * `field_auth_events`, alleen `headers_ontbreken`: calls die vertrokken
 * terwijl de store al leeg was. De sessie sneuvelde dus lokaal, hij werd niet
 * geweigerd.
 *
 * De oude reden voor `sessionStorage` was gedeelde tablets: de volgende persoon
 * krijgt een schoon loginscherm. De field-app draait in de praktijk op
 * persoonlijke telefoons in een autohouder (de twee kiosk-apparaten zijn van de
 * office-app). Die hygiëne houden we vast met een inactiviteitsklok in plaats
 * van met de levensduur van een tabblad: na SESSIE_MAX_INACTIEF_MS zonder
 * activiteit is de sessie weg, en de knop 'Uitloggen' blijft gewoon werken.
 *
 * Backwards compat: `user` en `error` aliases voor pagina's die ze nog gebruiken.
 */

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

/**
 * Zonder activiteit vervalt de sessie. Ruim genoeg voor een werkdag met pauzes
 * en een telefoon die tussendoor op zwart gaat, kort genoeg dat een apparaat
 * dat blijft liggen zichzelf de volgende dag afmeldt.
 */
const SESSIE_MAX_INACTIEF_MS = 12 * 60 * 60 * 1000;

/**
 * De activiteitsklok wordt bij élke geauthenticeerde call aangeraakt. Niet elke
 * call naar localStorage schrijven: dat is een synchrone schrijf op de hot path
 * van een app die in bursts tot tien calls per seconde doet.
 */
const ACTIVITEIT_SCHRIJF_INTERVAL_MS = 60 * 1000;

export interface Staff {
  id: string;
  name: string;
  email: string | null;
  phone: string | null;
  team: string | null;
  contract_type: string;
  sf_medewerker_id: string | null;
}

/** Backwards compat: pages reference user.name, user.email, etc. */
export interface LegacyUser {
  id: string;
  user_id: string;
  name: string;
  email: string | null;
  username: string | null;
  photos?: { thumbnail?: string; picture?: string };
}

interface AuthState {
  // State
  staff: Staff | null;
  user: LegacyUser | null;
  sfMedewerkerId: string | null;
  pin: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  authError: string | null;
  error: string | null;
  /** Epoch-ms van de laatste geauthenticeerde actie; voedt de inactiviteitsklok. */
  laatsteActiviteitOp: number | null;

  // Actions
  login: (staffId: string, pin: string) => Promise<boolean>;
  logout: () => void;
  initialize: () => Promise<void>;
  handleCallback: (searchParams: URLSearchParams) => Promise<void>;
  fetchStaffList: () => Promise<Staff[]>;
  clearError: () => void;
  /** Zet de inactiviteitsklok vooruit. Goedkoop: schrijft hooguit eens per minuut. */
  raakSessieAan: () => void;
  /** Logt uit als de sessie te lang stil lag. True als er is uitgelogd. */
  controleerSessieVerval: () => boolean;
}

/** True zolang de sessie binnen het inactiviteitsvenster valt. */
function sessieNogGeldig(laatsteActiviteitOp: number | null): boolean {
  if (!laatsteActiviteitOp) return false;
  return Date.now() - laatsteActiviteitOp < SESSIE_MAX_INACTIEF_MS;
}

const OPSLAG_SLEUTEL = 'kleda-field-auth';

/**
 * localStorage met eenmalige overname uit sessionStorage.
 *
 * Zonder die overname zou de deploy zelf iedereen die op dat moment ingelogd
 * is één keer uit de app gooien, precies de klacht die we aan het oplossen
 * zijn. Wie nog een levend tabblad heeft, verhuist stil mee. De oude sleutel
 * wordt daarna opgeruimd zodat dit pad niet blijft bestaan.
 */
const sessieOpslag: Storage = {
  get length() {
    return localStorage.length;
  },
  clear: () => localStorage.clear(),
  key: (index: number) => localStorage.key(index),
  removeItem: (naam: string) => localStorage.removeItem(naam),
  setItem: (naam: string, waarde: string) => localStorage.setItem(naam, waarde),
  getItem: (naam: string) => {
    const uitLocal = localStorage.getItem(naam);
    if (uitLocal !== null) return uitLocal;

    if (naam !== OPSLAG_SLEUTEL) return null;
    const uitSessie = sessionStorage.getItem(naam);
    if (uitSessie === null) return null;

    localStorage.setItem(naam, uitSessie);
    sessionStorage.removeItem(naam);
    return uitSessie;
  },
};

function staffToUser(staff: Staff | null): LegacyUser | null {
  if (!staff) return null;
  return {
    id: staff.id,
    user_id: staff.id,
    name: staff.name,
    email: staff.email,
    username: staff.email,
  };
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      staff: null,
      user: null,
      sfMedewerkerId: null,
      pin: null,
      isAuthenticated: false,
      isLoading: false,
      authError: null,
      error: null,
      laatsteActiviteitOp: null,

      login: async (staffId: string, pin: string) => {
        set({ isLoading: true, authError: null, error: null });
        try {
          const res = await fetch('/api/auth', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ staff_id: staffId, pin }),
          });

          if (!res.ok) {
            const err = await res.json().catch(() => ({ error: 'Login failed' }));
            const msg = res.status === 401 ? 'Ongeldige PIN' : err.error || 'Login mislukt';
            set({ isLoading: false, authError: msg, error: msg });
            return false;
          }

          const data = await res.json();
          const staff = data.staff as Staff;
          set({
            staff,
            user: staffToUser(staff),
            sfMedewerkerId: staff.sf_medewerker_id,
            pin,
            isAuthenticated: true,
            isLoading: false,
            authError: null,
            error: null,
            laatsteActiviteitOp: Date.now(),
          });
          return true;
        } catch (error) {
          const msg = error instanceof Error ? error.message : 'Login mislukt';
          set({ isLoading: false, authError: msg, error: msg });
          return false;
        }
      },

      logout: () => {
        set({
          staff: null,
          user: null,
          sfMedewerkerId: null,
          pin: null,
          isAuthenticated: false,
          isLoading: false,
          authError: null,
          error: null,
          laatsteActiviteitOp: null,
        });
      },

      raakSessieAan: () => {
        const { isAuthenticated, laatsteActiviteitOp } = get();
        if (!isAuthenticated) return;
        const nu = Date.now();
        if (laatsteActiviteitOp && nu - laatsteActiviteitOp < ACTIVITEIT_SCHRIJF_INTERVAL_MS) {
          return;
        }
        set({ laatsteActiviteitOp: nu });
      },

      controleerSessieVerval: () => {
        const { isAuthenticated, laatsteActiviteitOp } = get();
        if (!isAuthenticated) return false;
        if (sessieNogGeldig(laatsteActiviteitOp)) return false;
        get().logout();
        return true;
      },

      initialize: async () => {},
      handleCallback: async () => {},

      fetchStaffList: async () => {
        try {
          const res = await fetch('/api/auth');
          if (!res.ok) return [];
          return await res.json();
        } catch {
          return [];
        }
      },

      clearError: () => set({ authError: null, error: null }),
    }),
    {
      name: OPSLAG_SLEUTEL,
      storage: createJSONStorage(() => sessieOpslag),
      partialize: (state) => ({
        staff: state.staff,
        user: state.user,
        sfMedewerkerId: state.sfMedewerkerId,
        pin: state.pin,
        isAuthenticated: state.isAuthenticated,
        laatsteActiviteitOp: state.laatsteActiviteitOp,
      }),
      // De klok is de enige poort naar een herstelde sessie. Een blob zonder
      // tijdstempel komt uit de sessionStorage-migratie en krijgt eenmalig het
      // voordeel van de twijfel: die kwam per definitie uit het tabblad dat op
      // dit moment openstaat.
      onRehydrateStorage: () => (state) => {
        if (!state?.isAuthenticated) return;
        if (!state.laatsteActiviteitOp) {
          state.laatsteActiviteitOp = Date.now();
          return;
        }
        if (!sessieNogGeldig(state.laatsteActiviteitOp)) {
          state.staff = null;
          state.user = null;
          state.sfMedewerkerId = null;
          state.pin = null;
          state.isAuthenticated = false;
          state.laatsteActiviteitOp = null;
        }
      },
    }
  )
);
